import React from "react";
import { InView } from 'react-intersection-observer';

const Vision = () => {
  const visionPillars = [
    {
      icon: "🚀",
      title: "Redefine Experiences",
      description: "Transform car rentals into effortless, accessible, and enjoyable experiences"
    },
    {
      icon: "🌍",
      title: "Global Accessibility",
      description: "Make premium vehicle rentals accessible to everyone, everywhere"
    },
    {
      icon: "💡",
      title: "Innovation Driven",
      description: "Leverage technology to simplify booking and enhance customer journeys"
    },
    {
      icon: "⭐",
      title: "Service Excellence",
      description: "Set new standards in customer service and satisfaction"
    }
  ];

  return (
    <div className="py-20 bg-gradient-to-br from-purple-900 via-blue-800 to-cyan-700 relative overflow-hidden">
      {/* Background Elements */}
      <div className="absolute inset-0 opacity-20">
        <div className="absolute top-0 left-0 w-80 h-80 bg-orange-500 rounded-full mix-blend-screen filter blur-3xl animate-pulse"></div>
        <div className="absolute bottom-0 right-0 w-80 h-80 bg-red-500 rounded-full mix-blend-screen filter blur-3xl animate-pulse animation-delay-2000"></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-yellow-500 rounded-full mix-blend-screen filter blur-3xl animate-pulse animation-delay-4000"></div>
      </div>

      <div className="container mx-auto px-4 relative z-10">
        <div className="flex flex-col lg:flex-row items-center gap-12">
          {/* Left Section - Enhanced Content */}
          <InView triggerOnce={true} threshold={0.3}>
            {({ inView, ref }) => (
              <div
                ref={ref}
                className={`lg:w-1/2 ${inView ? 'animate__animated animate__fadeInLeft' : ''}`}
              >
                <div className="bg-white/10 backdrop-blur-md rounded-2xl p-8 border border-white/20 shadow-2xl">
                  {/* Section Header */}
                  <div className="mb-8 text-center lg:text-left">
                    <h2 className="text-4xl lg:text-5xl font-bold text-white mb-4">
                      Our <span className="bg-gradient-to-r from-orange-400 to-yellow-400 bg-clip-text text-transparent">Vision</span>
                    </h2>
                    <div className="w-16 h-1 bg-gradient-to-r from-orange-400 to-yellow-400 rounded-full mx-auto lg:mx-0 mb-6"></div>
                    
                    <p className="text-xl text-white/90 leading-relaxed">
                      To <span className="font-semibold text-yellow-300">revolutionize</span> the future of transportation by making premium vehicle rentals accessible, seamless, and empowering for every traveler.
                    </p>
                  </div>

                  {/* Vision Statement */}
                  <div className="mb-8">
                    <div className="bg-white/5 rounded-xl p-6 border border-white/10 mb-6">
                      <p className="text-white/80 leading-relaxed text-center lg:text-left">
                        At <span className="font-semibold text-orange-300">Nupura Self Drive Cars</span>, we envision a world where renting a vehicle is as simple and intuitive as clicking a button, 
                        where every journey begins with excitement and ends with satisfaction.
                      </p>
                    </div>
                  </div>

                  {/* Vision Pillars Grid */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
                    {visionPillars.map((pillar, index) => (
                      <div 
                        key={index}
                        className="bg-white/5 backdrop-blur-sm rounded-xl p-4 border border-white/10 hover:border-orange-400/30 transition-all duration-300 group hover:bg-white/10"
                      >
                        <div className="flex items-start space-x-3">
                          <div className="flex-shrink-0 w-12 h-12 bg-gradient-to-r from-orange-500 to-yellow-500 rounded-lg flex items-center justify-center text-white text-xl group-hover:scale-110 transition-transform duration-300 shadow-lg">
                            {pillar.icon}
                          </div>
                          <div>
                            <h3 className="text-white font-bold text-lg mb-1">{pillar.title}</h3>
                            <p className="text-white/70 text-sm">{pillar.description}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Future Goals */}
                  <div className="bg-gradient-to-r from-orange-600/20 to-yellow-600/20 rounded-xl p-6 border border-orange-400/30">
                    <h4 className="text-white font-bold text-lg mb-3 text-center">Our Future Goals</h4>
                    <div className="flex flex-wrap justify-center gap-3">
                      {["AI-Powered Booking", "Global Expansion", "Electric Fleet", "24/7 Support"].map((goal, index) => (
                        <span key={index} className="bg-white/10 text-white/90 px-3 py-1 rounded-full text-sm border border-white/20">
                          {goal}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>

                {/* CTA Section */}
                <div className="mt-6 text-center lg:text-left">
                  <button className="bg-gradient-to-r from-orange-500 to-yellow-500 hover:from-orange-600 hover:to-yellow-600 text-white font-bold py-3 px-8 rounded-full shadow-2xl hover:shadow-3xl transform hover:scale-105 transition-all duration-300">
                    Be Part of Our Vision
                  </button>
                </div>
              </div>
            )}
          </InView>

          {/* Right Section - Enhanced Image */}
          <InView triggerOnce={true} threshold={0.3}>
            {({ inView, ref }) => (
              <div
                ref={ref}
                className={`lg:w-1/2 relative ${inView ? 'animate__animated animate__fadeInRight' : ''}`}
              >
                <div className="relative group">
                  {/* Main Image Container with Glow */}
                  <div className="relative rounded-2xl overflow-hidden border-2 border-white/20 backdrop-blur-sm shadow-2xl">
                    <img
                      src="https://static.vecteezy.com/system/resources/previews/010/902/081/large_2x/business-analytics-in-cloud-arrow-leadership-company-people-challenge-teamwork-up-flat-job-marketing-concept-illustration-growth-with-rocket-investment-service-man-and-woman-trend-result-vector.jpg"
                      alt="Our Vision"
                      className="w-full h-[500px] object-cover transform group-hover:scale-105 transition-transform duration-700"
                    />
                    
                    {/* Overlay Gradient */}
                    <div className="absolute inset-0 bg-gradient-to-t from-orange-900/40 to-transparent"></div>
                    
                    {/* Floating Content */}
                    <div className="absolute bottom-6 left-6 right-6">
                      <div className="bg-white/10 backdrop-blur-md rounded-xl p-4 border border-white/20">
                        <h3 className="text-white font-bold text-lg mb-2">Building the Future Together</h3>
                        <p className="text-white/80 text-sm">Join us in creating tomorrow's transportation solutions</p>
                      </div>
                    </div>
                  </div>

                  {/* Background Glow */}
                  <div className="absolute -inset-4 bg-gradient-to-r from-orange-500 to-yellow-500 rounded-2xl blur-xl opacity-30 group-hover:opacity-50 transition duration-1000 group-hover:duration-200 -z-10"></div>

                  {/* Floating Elements */}
                  <div className="absolute -top-4 -right-4 bg-gradient-to-r from-orange-500 to-yellow-500 text-white py-2 px-4 rounded-full shadow-2xl transform rotate-6">
                    <span className="text-sm font-bold">Future Ready</span>
                  </div>

                  <div className="absolute -bottom-4 -left-4 bg-gradient-to-r from-red-500 to-pink-500 text-white py-2 px-4 rounded-full shadow-2xl transform -rotate-6">
                    <span className="text-sm font-bold">Innovation</span>
                  </div>
                </div>

                {/* Vision Metrics */}
                <div className="grid grid-cols-3 gap-4 mt-6">
                  <div className="text-center bg-white/5 backdrop-blur-sm rounded-lg p-4 border border-white/10 hover:bg-white/10 transition-all duration-300">
                    <div className="text-2xl font-bold text-orange-400">50+</div>
                    <div className="text-white/80 text-sm">Cities Target</div>
                  </div>
                  <div className="text-center bg-white/5 backdrop-blur-sm rounded-lg p-4 border border-white/10 hover:bg-white/10 transition-all duration-300">
                    <div className="text-2xl font-bold text-yellow-400">2025</div>
                    <div className="text-white/80 text-sm">Expansion Goal</div>
                  </div>
                  <div className="text-center bg-white/5 backdrop-blur-sm rounded-lg p-4 border border-white/10 hover:bg-white/10 transition-all duration-300">
                    <div className="text-2xl font-bold text-red-400">1M+</div>
                    <div className="text-white/80 text-sm">Customers Goal</div>
                  </div>
                </div>
              </div>
            )}
          </InView>
        </div>
      </div>

      {/* Custom CSS for animations */}
      <style jsx>{`
        .animation-delay-2000 {
          animation-delay: 2s;
        }
        .animation-delay-4000 {
          animation-delay: 4s;
        }
      `}</style>
    </div>
  );
};

export default Vision;